import { useMemo } from 'react';

import { BADGE_DESCRIPTIONS } from 'features/catalog/badge-definitions';
import { JOURNEYS, Journey, badgeIdsInJourney, isJourneyCompleted } from 'features/catalog/badge-journeys';
import { liveCatalogBadgeIDs } from 'features/catalog/badge-state';
import { AN_HOUR_MS, FEED_BURST_MS } from 'config/constants';
import { getFirstPartOfEmail } from 'shared/lib/email';
import { asRarityName, compareBadgesByScoreAsc } from 'features/catalog/rarity';
import {
  CONTRIBUTION_AWARD_THRESHOLDS,
  SYSTEM_AWARD_BADGE_IDS,
  getAutoUpdatedMap,
} from 'shared/hooks/getAutoUpdatedMap';
import { Badge } from 'features/catalog/useBadges';
import { CATALOG_FEED_PROFILE, badgeCreditedToLocal, type FeedUpdate } from 'features/feed/types';
import { Profile, RawSubmission } from 'features/profiles/useProfiles';

import {
  DEFAULT_BADGE_DATE_ADDED,
  catalogMsNudgedBelowEarlierSameDayClaim,
  isBadgeReleased,
  isDateAddedVisible,
  parseDateAdded,
} from 'features/catalog/badge-release';

export {
  DEFAULT_BADGE_DATE_ADDED,
  isBadgeReleased,
  isDateAddedVisible,
  parseDateAdded,
} from 'features/catalog/badge-release';
export { CATALOG_FEED_PROFILE, badgeCreditedToLocal, feedUpdateKey, type FeedUpdate } from 'features/feed/types';

const bucketBadges = (badgeIDs: string[], badgesMap: Map<string, Badge>) => {
  const badgesByRarity = {
    mythic: [] as Badge[],
    legendary: [] as Badge[],
    rare: [] as Badge[],
    common: [] as Badge[],
  };

  badgeIDs.forEach((badgeId) => {
    const badge = badgesMap.get(badgeId);
    if (!badge) return;
    badgesByRarity[asRarityName(badge.rarityName)].push(badge);
  });

  const sortedNewBadges = [
    ...badgesByRarity.mythic,
    ...badgesByRarity.legendary,
    ...badgesByRarity.rare,
    ...badgesByRarity.common,
  ].sort(compareBadgesByScoreAsc);

  return { badgesByRarity, sortedNewBadges };
};

const journeysCompletedBy = (previousBadgeIDs: string[], currentBadgeIDs: string[]): Journey[] => {
  const previousOwned = new Set(previousBadgeIDs);
  const currentOwned = new Set(currentBadgeIDs);
  return JOURNEYS.filter(
    (journey) => !isJourneyCompleted(journey, previousOwned) && isJourneyCompleted(journey, currentOwned)
  );
};

/** A card may only announce a journey if this event actually granted/claimed a badge on it. */
const journeyTouchedByBadges = (journey: Journey, badgeIDs: readonly string[]): boolean => {
  if (badgeIDs.length === 0) return false;
  const inJourney = new Set(badgeIdsInJourney(journey));
  return badgeIDs.some((id) => inJourney.has(id));
};

const journeysCompletedOnCard = (
  previousBadgeIDs: string[],
  currentBadgeIDs: string[],
  newBadgeIDs: readonly string[]
): Journey[] =>
  journeysCompletedBy(previousBadgeIDs, currentBadgeIDs).filter((journey) =>
    journeyTouchedByBadges(journey, newBadgeIDs)
  );

/** Newest-first. Same timestamp → grants above claims; catalog drops below both. */
const compareFeedUpdatesDesc = (a: FeedUpdate, b: FeedUpdate): number => {
  const timeDiff = b.eventAt.getTime() - a.eventAt.getTime();
  if (timeDiff !== 0) return timeDiff;
  if (!!a.isAutoGrant !== !!b.isAutoGrant) {
    return a.isAutoGrant ? -1 : 1;
  }
  if (!!a.isCatalogAddition !== !!b.isCatalogAddition) {
    return a.isCatalogAddition ? 1 : -1;
  }
  return 0;
};

const sameLocalCalendarDay = (aMs: number, bMs: number): boolean => {
  const a = new Date(aMs);
  const b = new Date(bMs);
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

/**
 * Date-only catalog drops stamp at local noon and can sort *above* earlier
 * same-day claims. Nudge only then (see `catalogMsNudgedBelowEarlierSameDayClaim`).
 */
const placeCatalogDropsBelowFirstClaims = (updates: FeedUpdate[]): FeedUpdate[] => {
  const earliestPersonEventMsByBadge = new Map<string, number>();
  for (const update of updates) {
    if (update.isCatalogAddition) continue;
    const ms = update.eventAt.getTime();
    for (const id of update.newBadges) {
      const prev = earliestPersonEventMsByBadge.get(id);
      if (prev == null || ms < prev) earliestPersonEventMsByBadge.set(id, ms);
    }
  }

  return updates.map((update) => {
    if (!update.isCatalogAddition) return update;
    const catalogMs = update.eventAt.getTime();
    let firstClaimMs: number | null = null;
    for (const id of update.newBadges) {
      const ms = earliestPersonEventMsByBadge.get(id);
      if (ms == null || !sameLocalCalendarDay(ms, catalogMs)) continue;
      if (firstClaimMs == null || ms < firstClaimMs) firstClaimMs = ms;
    }
    const eventMs = catalogMsNudgedBelowEarlierSameDayClaim(catalogMs, firstClaimMs);
    if (eventMs === catalogMs) return update;
    return { ...update, eventAt: new Date(eventMs) };
  });
};

/**
 * Badger / Baron unlock when a contribution is catalogued. After catalog drops may be
 * nudged below same-day claims, keep those grants strictly fresher than the contribution
 * row they came from (so “granted Badger” never sorts under “contributed”).
 */
const placeContributionAwardsAboveTheirCatalog = (updates: FeedUpdate[]): FeedUpdate[] => {
  const catalogMsByBadge = new Map<string, number>();
  for (const update of updates) {
    if (!update.isCatalogAddition) continue;
    const ms = update.eventAt.getTime();
    for (const id of update.newBadges) catalogMsByBadge.set(id, ms);
  }

  return updates
    .map((update) => {
      if (!update.isAutoGrant || update.isCatalogAddition) return update;
      const recipients = update.grantedToProfiles?.length ? update.grantedToProfiles : [update.profile];
      let eventMs = update.eventAt.getTime();

      for (const awardId of update.newBadges) {
        const threshold = CONTRIBUTION_AWARD_THRESHOLDS[awardId];
        if (threshold == null) continue;

        for (const person of recipients) {
          const contribTimes = [
            ...new Set(person.submittedBadgeIDs.filter((id) => id !== awardId && catalogMsByBadge.has(id))),
          ]
            .map((id) => catalogMsByBadge.get(id)!)
            .sort((a, b) => a - b);
          if (contribTimes.length < threshold) continue;
          const unlockCatalogMs = contribTimes[threshold - 1];
          if (eventMs <= unlockCatalogMs) eventMs = unlockCatalogMs + 1;
        }
      }

      if (eventMs === update.eventAt.getTime()) return update;
      return { ...update, eventAt: new Date(eventMs) };
    })
    .sort(compareFeedUpdatesDesc);
};

/** Catalog `dateAdded` → ms; parsed once per badge id for the process lifetime. */
const badgeAddedMsCache = new Map<string, number>();
const badgeAddedMs = (badgeId: string): number => {
  const cached = badgeAddedMsCache.get(badgeId);
  if (cached != null) return cached;
  const dateAdded = BADGE_DESCRIPTIONS.get(badgeId)?.dateAdded ?? DEFAULT_BADGE_DATE_ADDED;
  const ms = parseDateAdded(dateAdded).getTime();
  badgeAddedMsCache.set(badgeId, ms);
  return ms;
};

/** Auto-claim milestones (Explorer / Always Down / etc.): fold into claims when the badge
 *  already existed at unlock; emit a catalog-time grant only if it shipped after they qualified. */
const isAutoClaimMilestoneBadge = (badge: Badge | undefined): boolean =>
  !!badge?.banSelfClaim && !!badge.autoClaimRules?.ids.length;

/** Winner lists, Badger/Baron, QA, and other pure `banSelfClaim` — always “granted” in feed. */
const isFeedGrantOnlyBadge = (badgeId: string, badgesMap: Map<string, Badge>): boolean => {
  const badge = badgesMap.get(badgeId);
  if (isAutoClaimMilestoneBadge(badge)) return false;
  return SYSTEM_AWARD_BADGE_IDS.has(badgeId) || !!badge?.banSelfClaim;
};

const autoClaimMilestoneDepsMet = (badge: Badge, sheetIds: ReadonlySet<string>): boolean => {
  const rules = badge.autoClaimRules;
  if (!rules) return false;
  return rules.ids.filter((depId) => sheetIds.has(depId)).length >= rules.count;
};

/** Catalog-wave grant: stamp when the badge was added (or just after join if they joined later). */
const grantMsAtCatalogOrJoin = (addedMs: number, joinMs: number): number => (joinMs <= addedMs ? addedMs : joinMs + 1);

/** Progress grant (Badger / Baron): when the Nth contribution landed (never before join). */
const grantMsAtProgress = (criteriaMs: number, addedMs: number, joinMs: number): number => {
  const floor = Math.max(criteriaMs, addedMs, joinMs);
  return floor <= joinMs ? joinMs + 1 : floor;
};

/** Sorted catalog times for unique contributed badge ids (Badger / Baron unlock math). */
const contributionCatalogTimes = (contributedBadgeIds: readonly string[], badgesMap: Map<string, Badge>): number[] => {
  const times: number[] = [];
  const seen = new Set<string>();
  for (const id of contributedBadgeIds) {
    // Award badges themselves never count toward their own thresholds.
    if (CONTRIBUTION_AWARD_THRESHOLDS[id] != null || seen.has(id) || !badgesMap.has(id)) continue;
    if (!isBadgeReleased(id)) continue;
    seen.add(id);
    times.push(badgeAddedMs(id));
  }
  times.sort((a, b) => a - b);
  return times;
};

/**
 * When this grant should appear in the feed.
 * Catalog-style awards → badge dateAdded (after join if needed).
 * Contribution awards → when the Nth contributed badge was catalogued.
 */
const grantEventMsForBadge = (
  badgeId: string,
  badgesMap: Map<string, Badge>,
  contribTimesSorted: readonly number[],
  joinMs: number
): number | null => {
  const badge = badgesMap.get(badgeId);
  if (!badge) return null;
  const dateAdded = BADGE_DESCRIPTIONS.get(badgeId)?.dateAdded ?? DEFAULT_BADGE_DATE_ADDED;
  if (!isDateAddedVisible(dateAdded)) return null;
  const addedMs = parseDateAdded(dateAdded).getTime();

  const contributionThreshold = CONTRIBUTION_AWARD_THRESHOLDS[badgeId];
  if (contributionThreshold != null) {
    if (contribTimesSorted.length < contributionThreshold) return null;
    return grantMsAtProgress(contribTimesSorted[contributionThreshold - 1], addedMs, joinMs);
  }

  // Pure banSelfClaim (racing, etc.): catalog drop, or just after join.
  return grantMsAtCatalogOrJoin(addedMs, joinMs);
};

/**
 * Expand a raw sheet badge list the same way profiles do (auto-claim / restrictions),
 * so journey completion matches the Journeys tab — including journeys added later.
 *
 * When `atMs` is set, auto-claim rules for badges catalogued after that time are ignored
 * (unless the id was already on the sheet row) so historical claims don’t absorb later tiers.
 *
 * Hot path: `getAutoUpdatedMap` walks the full catalog (cascades). Callers must not
 * re-expand the same sheet row + timestamp more than once.
 */
const expandOwnedBadgeIDs = (
  badgeIDs: string[],
  badgesMap: Map<string, Badge>,
  profile: Pick<Profile, 'email' | 'submittedBadgeIDs'>,
  atMs?: number
): string[] => {
  const sheetOwned = new Map<string, boolean>();
  for (const id of badgeIDs) sheetOwned.set(id, true);
  const enabled = getAutoUpdatedMap(sheetOwned, badgesMap, {
    email: profile.email,
    submittedBadgeIDs: profile.submittedBadgeIDs,
  });
  const onSheet = new Set(badgeIDs);
  const out: string[] = [];
  for (const [id, on] of enabled) {
    if (!on) continue;
    if (atMs != null && !onSheet.has(id)) {
      const badge = badgesMap.get(id);
      if (badge?.autoClaimRules && badgeAddedMs(id) > atMs) continue;
    }
    out.push(id);
  }
  return out;
};

/** Collapse a same-person burst into one card (latest timestamp; join wording if any was a join). */
const collapseUpdateBurst = (group: FeedUpdate[], badgesMap: Map<string, Badge>): FeedUpdate => {
  if (group.length === 1) return group[0];

  group.sort((a, b) => a.eventAt.getTime() - b.eventAt.getTime());
  const latest = group[group.length - 1];

  const seenBadges = new Set<string>();
  const newBadges: string[] = [];
  for (const update of group) {
    for (const id of update.newBadges) {
      if (seenBadges.has(id)) continue;
      seenBadges.add(id);
      newBadges.push(id);
    }
  }

  const seenJourneys = new Set<string>();
  const completedJourneys: Journey[] = [];
  for (const update of group) {
    for (const journey of update.completedJourneys) {
      if (seenJourneys.has(journey.id)) continue;
      seenJourneys.add(journey.id);
      completedJourneys.push(journey);
    }
  }

  return {
    profile: latest.profile,
    eventAt: latest.eventAt,
    newBadges,
    ...bucketBadges(newBadges, badgesMap),
    // Join + claim within the burst window → still "joined with N badges".
    isNewUser: group.some((u) => u.isNewUser),
    completedJourneys,
    positionsMovedUp: [...group].reverse().find((u) => u.positionsMovedUp != null)?.positionsMovedUp,
  };
};

/**
 * Merge same-person events that fall within 12 hours of the burst's first event
 * into one feed card (joins included — later claims fold into "joined with N").
 */
const eventMsBetween = (lo: number, hi: number, times: readonly number[]): boolean => {
  if (hi < lo) {
    const swap = lo;
    lo = hi;
    hi = swap;
  }
  return times.some((t) => t > lo && t < hi);
};

const mergeUpdatesInBurstWindow = (updates: FeedUpdate[], badgesMap: Map<string, Badge>): FeedUpdate[] => {
  /** Catalog drops + auto-grants stay as their own cards (never fold into “claimed”). */
  const standalone: FeedUpdate[] = [];
  const byEmail = new Map<string, FeedUpdate[]>();

  for (const update of updates) {
    if (update.isCatalogAddition || update.isAutoGrant) {
      standalone.push(update);
      continue;
    }
    const key = update.profile.email;
    const list = byEmail.get(key);
    if (list) list.push(update);
    else byEmail.set(key, [update]);
  }

  const merged: FeedUpdate[] = [];
  for (const personUpdates of byEmail.values()) {
    personUpdates.sort((a, b) => a.eventAt.getTime() - b.eventAt.getTime());

    let burst: FeedUpdate[] = [];
    let burstStartMs = 0;

    const flush = () => {
      if (burst.length === 0) return;
      merged.push(collapseUpdateBurst(burst, badgesMap));
      burst = [];
    };

    for (const update of personUpdates) {
      const t = update.eventAt.getTime();
      if (burst.length === 0) {
        burst = [update];
        burstStartMs = t;
        continue;
      }
      if (t - burstStartMs < FEED_BURST_MS) {
        burst.push(update);
      } else {
        flush();
        burst = [update];
        burstStartMs = t;
      }
    }
    flush();
  }

  return [...standalone, ...merged].sort(compareFeedUpdatesDesc);
};

/**
 * Collapse auto-grants of the same badge into one card when they land near each other.
 * Grants are emitted one badge per card so mixed awards (World Walker + Woodloch) still
 * join their respective same-badge waves. Timestamps often differ slightly, so we:
 * 1) Pull everyone within 48h of a shared catalog dateAdded into one wave
 * 2) Cluster any leftovers by consecutive 48h gaps
 */
const mergeSameMomentAutoGrants = (updates: FeedUpdate[]): FeedUpdate[] => {
  const grants: FeedUpdate[] = [];
  const rest: FeedUpdate[] = [];
  for (const update of updates) {
    if (update.isAutoGrant && !update.isCatalogAddition) grants.push(update);
    else rest.push(update);
  }

  const badgeKey = (ids: string[]) =>
    [...ids]
      .map((id) => id.trim())
      .filter(Boolean)
      .sort()
      .join(',');

  const byBadges = new Map<string, FeedUpdate[]>();
  for (const grant of grants) {
    // Keep journey-completing grants on a personal card — a multi-person wave
    // hides the note, which made completed journeys vanish from the feed.
    if (grant.completedJourneys.length > 0) {
      rest.push(grant);
      continue;
    }
    const key = badgeKey(grant.newBadges);
    if (!key) {
      rest.push(grant);
      continue;
    }
    const list = byBadges.get(key);
    if (list) list.push(grant);
    else byBadges.set(key, [grant]);
  }

  const mergeWindowMs = 48 * AN_HOUR_MS;
  const separatorMs = rest.map((u) => u.eventAt.getTime());

  const sharedCatalogMs = (ids: string[]): number | null => {
    let catalog: string | null = null;
    for (const id of ids) {
      const dateAdded = BADGE_DESCRIPTIONS.get(id)?.dateAdded ?? DEFAULT_BADGE_DATE_ADDED;
      if (catalog == null) catalog = dateAdded;
      else if (catalog !== dateAdded) return null;
    }
    return catalog ? parseDateAdded(catalog).getTime() : null;
  };

  const collapseCluster = (cluster: FeedUpdate[]): FeedUpdate => {
    if (cluster.length === 1) return cluster[0];
    const seenEmails = new Set<string>();
    const grantedToProfiles: Profile[] = [];
    let latest = cluster[0];
    for (const update of cluster) {
      if (update.eventAt.getTime() > latest.eventAt.getTime()) latest = update;
      // Already-merged cards contribute all their recipients.
      const people = update.grantedToProfiles?.length ? update.grantedToProfiles : [update.profile];
      for (const person of people) {
        if (seenEmails.has(person.email)) continue;
        seenEmails.add(person.email);
        grantedToProfiles.push(person);
      }
    }
    grantedToProfiles.sort((a, b) => a.name.localeCompare(b.name));
    return {
      ...latest,
      profile: grantedToProfiles[0],
      grantedToProfiles,
      eventAt: latest.eventAt,
      // Multi-person waves can't name who finished a journey — drop the note.
      completedJourneys: grantedToProfiles.length > 1 ? [] : latest.completedJourneys,
    };
  };

  const clusterByGap = (group: FeedUpdate[]): FeedUpdate[] => {
    if (group.length === 0) return [];
    const sorted = [...group].sort((a, b) => a.eventAt.getTime() - b.eventAt.getTime());
    const out: FeedUpdate[] = [];
    let cluster: FeedUpdate[] = [];
    let clusterEndMs = 0;
    const flush = () => {
      if (cluster.length === 0) return;
      out.push(collapseCluster(cluster));
      cluster = [];
    };
    for (const grant of sorted) {
      const t = grant.eventAt.getTime();
      if (cluster.length === 0) {
        cluster = [grant];
        clusterEndMs = t;
        continue;
      }
      if (t - clusterEndMs <= mergeWindowMs && !eventMsBetween(clusterEndMs, t, separatorMs)) {
        cluster.push(grant);
        clusterEndMs = t;
      } else {
        flush();
        cluster = [grant];
        clusterEndMs = t;
      }
    }
    flush();
    return out;
  };

  const mergedGrants: FeedUpdate[] = [];
  for (const [key, group] of byBadges) {
    const ids = key.split(',');
    const catalogMs = sharedCatalogMs(ids);
    if (catalogMs != null) {
      const wave: FeedUpdate[] = [];
      const leftover: FeedUpdate[] = [];
      for (const grant of group) {
        if (Math.abs(grant.eventAt.getTime() - catalogMs) <= mergeWindowMs) wave.push(grant);
        else leftover.push(grant);
      }
      if (wave.length > 0) mergedGrants.push(collapseCluster(wave));
      mergedGrants.push(...clusterByGap(leftover));
    } else {
      mergedGrants.push(...clusterByGap(group));
    }
  }

  return [...rest, ...mergedGrants].sort(compareFeedUpdatesDesc);
};

/**
 * After same-badge cross-person waves merge, combine leftover single-recipient
 * grants for the same person on the same calendar day into one card (e.g.
 * Barcelona + Always Down from a CUSTOM grant). Multi-person waves stay intact.
 */
const mergeSamePersonSameDaySoloAutoGrants = (updates: FeedUpdate[], badgesMap: Map<string, Badge>): FeedUpdate[] => {
  const dayKey = (ms: number) => {
    const d = new Date(ms);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  };

  const isSoloGrant = (update: FeedUpdate): boolean =>
    !!update.isAutoGrant && !update.isCatalogAddition && (update.grantedToProfiles?.length ?? 1) <= 1;

  const soloByPersonDay = new Map<string, FeedUpdate[]>();
  const rest: FeedUpdate[] = [];

  for (const update of updates) {
    if (!isSoloGrant(update)) {
      rest.push(update);
      continue;
    }
    const key = `${update.profile.email}|${dayKey(update.eventAt.getTime())}`;
    const list = soloByPersonDay.get(key);
    if (list) list.push(update);
    else soloByPersonDay.set(key, [update]);
  }

  const mergedSolo: FeedUpdate[] = [];
  for (const group of soloByPersonDay.values()) {
    if (group.length === 1) {
      mergedSolo.push(group[0]);
      continue;
    }

    const seenBadges = new Set<string>();
    const newBadges: string[] = [];
    const seenJourneys = new Set<string>();
    const completedJourneys: Journey[] = [];
    let latest = group[0];
    for (const update of group) {
      if (update.eventAt.getTime() > latest.eventAt.getTime()) latest = update;
      for (const id of update.newBadges) {
        if (seenBadges.has(id)) continue;
        seenBadges.add(id);
        newBadges.push(id);
      }
      for (const journey of update.completedJourneys) {
        if (seenJourneys.has(journey.id)) continue;
        seenJourneys.add(journey.id);
        completedJourneys.push(journey);
      }
    }

    mergedSolo.push({
      profile: latest.profile,
      eventAt: latest.eventAt,
      newBadges,
      ...bucketBadges(newBadges, badgesMap),
      isNewUser: false,
      isAutoGrant: true,
      grantedToProfiles: undefined,
      completedJourneys,
    });
  }

  return [...rest, ...mergedSolo].sort(compareFeedUpdatesDesc);
};

/**
 * Profile feed: collapse this person's auto-grants that landed on the same calendar day
 * into one card (e.g. Always Down + Serial Packer), even when the main feed kept them
 * as separate same-badge waves across people.
 */
export const mergeProfileSameDayAutoGrants = (
  updates: FeedUpdate[],
  badgesMap: Map<string, Badge>,
  focusProfile: Profile
): FeedUpdate[] => {
  const focusId = getFirstPartOfEmail(focusProfile.email);
  const dayKey = (ms: number) => {
    const d = new Date(ms);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  };

  const involvesFocus = (update: FeedUpdate): boolean =>
    getFirstPartOfEmail(update.profile.email) === focusId ||
    !!update.grantedToProfiles?.some((p) => getFirstPartOfEmail(p.email) === focusId);

  const grantsByDay = new Map<string, FeedUpdate[]>();
  const rest: FeedUpdate[] = [];

  for (const update of updates) {
    if (!update.isAutoGrant || update.isCatalogAddition || !involvesFocus(update)) {
      rest.push(update);
      continue;
    }
    const key = dayKey(update.eventAt.getTime());
    const list = grantsByDay.get(key);
    if (list) list.push(update);
    else grantsByDay.set(key, [update]);
  }

  const mergedGrants: FeedUpdate[] = [];
  for (const group of grantsByDay.values()) {
    if (group.length === 1) {
      mergedGrants.push({
        ...group[0],
        profile: focusProfile,
        grantedToProfiles: undefined,
      });
      continue;
    }

    const seenBadges = new Set<string>();
    const newBadges: string[] = [];
    const seenJourneys = new Set<string>();
    const completedJourneys: Journey[] = [];
    let latest = group[0];
    for (const update of group) {
      if (update.eventAt.getTime() > latest.eventAt.getTime()) latest = update;
      for (const id of update.newBadges) {
        if (seenBadges.has(id)) continue;
        seenBadges.add(id);
        newBadges.push(id);
      }
      for (const journey of update.completedJourneys) {
        if (seenJourneys.has(journey.id)) continue;
        seenJourneys.add(journey.id);
        completedJourneys.push(journey);
      }
    }

    mergedGrants.push({
      profile: focusProfile,
      eventAt: latest.eventAt,
      newBadges,
      ...bucketBadges(newBadges, badgesMap),
      isNewUser: false,
      isAutoGrant: true,
      grantedToProfiles: undefined,
      completedJourneys,
    });
  }

  return [...rest, ...mergedGrants].sort(compareFeedUpdatesDesc);
};

/**
 * Profile Feed tab cards for one person — same inclusion + same-day grant merge as the
 * Feed (N) count on the profile header.
 */
export const getProfileFeedUpdates = (
  feedUpdates: FeedUpdate[],
  profile: Profile,
  badgesMap: Map<string, Badge>
): FeedUpdate[] => {
  const id = getFirstPartOfEmail(profile.email);
  const matched = feedUpdates.filter((u) => {
    if (u.isCatalogAddition) {
      return u.newBadges.some((badgeId) => {
        const badge = badgesMap.get(badgeId);
        return badge ? badgeCreditedToLocal(badge, id) : false;
      });
    }
    if (getFirstPartOfEmail(u.profile.email) === id) return true;
    // Batch custom grants share one card keyed by the first recipient — still count for others.
    return !!u.grantedToProfiles?.some((p) => getFirstPartOfEmail(p.email) === id);
  });
  return mergeProfileSameDayAutoGrants(matched, badgesMap, profile);
};

/**
 * Newest activity per profile id (email local-part): max of sheet `lastUpdated` and
 * claim / join / grant feed events. Catalog “contributed new badge” cards appear on
 * Profile Feed but do not count as last activity.
 */
export const buildProfileLastActivityById = (
  profiles: Iterable<Profile>,
  feedUpdates: FeedUpdate[],
  _badgesMap?: Map<string, Badge>
): Map<string, Date> => {
  const map = new Map<string, Date>();
  for (const profile of profiles) {
    map.set(getFirstPartOfEmail(profile.email), profile.lastUpdated);
  }

  const bump = (id: string, at: Date) => {
    const prev = map.get(id);
    if (prev == null || at.getTime() > prev.getTime()) map.set(id, at);
  };

  for (const update of feedUpdates) {
    // Idea/design credits on catalog drops stay on Profile Feed, but are not “updates”.
    if (update.isCatalogAddition) continue;
    const people = update.grantedToProfiles?.length ? update.grantedToProfiles : [update.profile];
    for (const person of people) {
      bump(getFirstPartOfEmail(person.email), update.eventAt);
    }
  }
  return map;
};

/**
 * Stamp `lastActivityAt` on every profile from feed events. Call once after
 * `useFeedUpdates` — keep raw sheet `lastUpdated` for grant/claim logic.
 */
export const withFeedActivityOnProfiles = (
  profilesMap: Map<string, Profile>,
  rankedProfiles: Profile[],
  feedUpdates: FeedUpdate[],
  badgesMap: Map<string, Badge>
): { profilesMap: Map<string, Profile>; rankedProfiles: Profile[] } => {
  const activityById = buildProfileLastActivityById(profilesMap.values(), feedUpdates, badgesMap);

  const nextMap = new Map<string, Profile>();
  let changed = false;
  for (const [id, profile] of profilesMap) {
    const lastActivityAt = activityById.get(id) ?? profile.lastUpdated;
    if (lastActivityAt.getTime() === profile.lastActivityAt.getTime()) {
      nextMap.set(id, profile);
    } else {
      changed = true;
      nextMap.set(id, { ...profile, lastActivityAt });
    }
  }
  if (!changed) return { profilesMap, rankedProfiles };

  const nextRanked = rankedProfiles.map((profile) => nextMap.get(getFirstPartOfEmail(profile.email)) ?? profile);
  return { profilesMap: nextMap, rankedProfiles: nextRanked };
};

/**
 * Derives feed update cards from already-fetched submission history.
 * Emits one event per submission that added badges, then merges same-person
 * events within 12 hours of the first in the burst into a single card (using
 * the latest timestamp so it stays near the top of the feed). A join plus later
 * claims in that window stay a join (“joined with N badges”).
 * The first sheet row for a person is always treated as account creation (join),
 * even when it’s the only entry — no second submission required.
 *
 * Journey completions are backtraced against *current* journey definitions.
 * Sheet rows are auto-expanded like profiles at the submission time (later
 * catalogued auto-claim tiers are ignored). Special-list awards and other
 * pure `banSelfClaim` badges always emit grant cards (never “claimed”).
 * Sheet rows with CUSTOM=`custom` grant the diff vs the previous submission;
 * those badge IDs stay grants forever (even on a later non-custom claim).
 * Auto-claim milestones unlocked on that same custom row (e.g. Always Down
 * tipping via a granted retreat) are grants too.
 * Auto-claim milestones (Explorer / Always Down / etc.) fold into the claim
 * when the badge already existed at unlock; they still “grant” at catalog
 * date when they shipped after deps were already met. Once on a sheet claim,
 * only that claim/join card is shown (no duplicate grant).
 * Catalog-wave grants stamp at badge dateAdded (or just after join);
 * Badger/Baron use the Nth contribution’s catalog date.
 * Remaining completions are credited on the first feed card whose badges
 * actually finish the journey — never an unrelated later claim.
 *
 * Catalog additions (badges with `dateAdded`) appear as Talos Badges system cards,
 * sorted just below the first same-day claim of those badges when one exists.
 */
export function buildFeedUpdates(
  submissionsByEmail: Map<string, RawSubmission[]>,
  profilesMap: Map<string, Profile>,
  badgesMap: Map<string, Badge>
): FeedUpdate[] {
  if (badgesMap.size === 0) return [];

  const updates: FeedUpdate[] = [];

  // Group catalog drops by dateAdded → one feed card per distinct stamp.
  // Badges without dateAdded default to March 2024.
  // Future dateAdded values stay hidden until that day (or instant, if timed).
  const catalogByDate = new Map<string, string[]>();
  BADGE_DESCRIPTIONS.forEach((desc, badgeId) => {
    if (!badgesMap.has(badgeId)) return;
    const dateAdded = desc.dateAdded ?? DEFAULT_BADGE_DATE_ADDED;
    if (!isDateAddedVisible(dateAdded)) return;
    const group = catalogByDate.get(dateAdded);
    if (group) group.push(badgeId);
    else catalogByDate.set(dateAdded, [badgeId]);
  });
  for (const [dateAdded, badgeIDs] of catalogByDate) {
    updates.push({
      profile: CATALOG_FEED_PROFILE,
      eventAt: parseDateAdded(dateAdded),
      newBadges: badgeIDs,
      ...bucketBadges(badgeIDs, badgesMap),
      isNewUser: false,
      isCatalogAddition: true,
      completedJourneys: [],
    });
  }

  if (submissionsByEmail.size === 0 || profilesMap.size === 0) {
    return updates.sort(compareFeedUpdatesDesc);
  }

  submissionsByEmail.forEach((submissions, email) => {
    const profile = profilesMap.get(getFirstPartOfEmail(email));
    if (!profile) return;

    const sorted = [...submissions].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    // Drop retired/unknown sheet IDs — same as profiles: treat as if they never existed.
    const liveSorted = sorted.map((s) => ({
      ...s,
      badgeIDs: liveCatalogBadgeIDs(s.badgeIDs, badgesMap),
    }));
    const personUpdates: FeedUpdate[] = [];
    const sheetIdSets = liveSorted.map((s) => new Set(s.badgeIDs));
    const profileBadgeIdSet = new Set(profile.badgesIDs);
    // Badger / Baron: compute once per person (not once per grant-only badge).
    const contribTimesSorted = contributionCatalogTimes(profile.submittedBadgeIDs, badgesMap);

    // Badges first introduced on a CUSTOM=`custom` row stay grants forever.
    // Milestones unlocked by that same custom row (e.g. Always Down via a
    // granted retreat) are grants too — not folded into a nearby claim card.
    // One chronological pass: expand each row once (was ~2–4× via separate custom/claim loops).
    const customGrantedIds = new Set<string>();
    const customGrantEventMsById = new Map<string, number>();
    const everOnSheet = new Set<string>();

    const currentOwnedByIndex: string[][] = [];

    liveSorted.forEach((submission, index) => {
      for (const id of submission.badgeIDs) everOnSheet.add(id);

      const atMs = submission.timestamp.getTime();
      const previousBadgeIDs = index === 0 ? [] : liveSorted[index - 1].badgeIDs;
      const previousSet = index === 0 ? null : sheetIdSets[index - 1];

      // Hot: full-catalog auto-claim expand — only once per submission.
      const previousOwnedFull = expandOwnedBadgeIDs(previousBadgeIDs, badgesMap, profile, atMs);
      const currentOwnedFull = expandOwnedBadgeIDs(submission.badgeIDs, badgesMap, profile, atMs);
      currentOwnedByIndex[index] = currentOwnedFull;
      const prevOwnedSet = new Set(previousOwnedFull);

      // Diff vs previous only — a custom-marked join is still a join.
      if (submission.isCustomGrant && index > 0 && previousSet) {
        for (const id of submission.badgeIDs) {
          if (previousSet.has(id) || customGrantedIds.has(id)) continue;
          // Contribution awards (Badger / Baron) keep dedicated grant timing.
          // Pure banSelfClaim sheet grants (racing, winners, QA, etc.) stamp at this CUSTOM row.
          if (SYSTEM_AWARD_BADGE_IDS.has(id)) continue;
          customGrantedIds.add(id);
          customGrantEventMsById.set(id, atMs);
        }
        for (const id of currentOwnedFull) {
          if (!isAutoClaimMilestoneBadge(badgesMap.get(id)) || prevOwnedSet.has(id)) continue;
          if (badgeAddedMs(id) > atMs || customGrantedIds.has(id)) continue;
          customGrantedIds.add(id);
          customGrantEventMsById.set(id, atMs);
        }
      }

      let newBadgeIDs: string[];
      let isNewUser = false;

      if (index === 0) {
        newBadgeIDs = submission.badgeIDs;
        isNewUser = true;
      } else {
        newBadgeIDs = submission.badgeIDs.filter((id) => !previousSet!.has(id));
      }

      // Special-list / banSelfClaim / CUSTOM grants never use “claimed” wording.
      newBadgeIDs = newBadgeIDs.filter((id) => !isFeedGrantOnlyBadge(id, badgesMap) && !customGrantedIds.has(id));
      const newBadgeIDSet = new Set(newBadgeIDs);

      const isLatest = index === sorted.length - 1;

      // Auto-claim milestones: if the badge already existed at this update,
      // fold the unlock into the claim/join card (not a separate “granted” card).
      // Skip milestones already marked as CUSTOM grants (unlocked by a grant row).
      for (const id of currentOwnedFull) {
        if (!isAutoClaimMilestoneBadge(badgesMap.get(id)) || prevOwnedSet.has(id)) continue;
        if (badgeAddedMs(id) > atMs || customGrantedIds.has(id)) continue;
        if (!newBadgeIDSet.has(id)) {
          newBadgeIDSet.add(id);
          newBadgeIDs.push(id);
        }
      }

      if (newBadgeIDs.length === 0) return;

      // Grant-only badges get their own grant cards — don’t credit journeys they unlock here.
      const previousOwned = previousOwnedFull.filter((id) => !isFeedGrantOnlyBadge(id, badgesMap));
      const currentOwned = currentOwnedFull.filter((id) => !isFeedGrantOnlyBadge(id, badgesMap));
      // Auto-claimed journey badges (e.g. Emerald Shield via a higher tenure claim) may
      // not be in newBadgeIDs — still count them as touching the journey on this card.
      const journeyTouchIds = [...newBadgeIDs];
      for (const id of currentOwnedFull) {
        if (prevOwnedSet.has(id)) continue;
        if (isFeedGrantOnlyBadge(id, badgesMap) || customGrantedIds.has(id)) continue;
        if (!journeyTouchIds.includes(id)) journeyTouchIds.push(id);
      }
      const completedJourneys = journeysCompletedOnCard(previousOwned, currentOwned, journeyTouchIds);

      personUpdates.push({
        profile,
        eventAt: submission.timestamp,
        newBadges: newBadgeIDs,
        ...bucketBadges(newBadgeIDs, badgesMap),
        isNewUser,
        completedJourneys,
        positionsMovedUp: isLatest ? profile.positionsMovedUp : undefined,
      });
    });

    // Grant cards: catalog-wave at dateAdded; progress awards at Nth contrib time.
    const joinMs = liveSorted[0]?.timestamp.getTime() ?? profile.lastUpdated.getTime();
    /** Already shown on a claim/join card (sheet diff or milestone fold). */
    const alreadyClaimedInFeed = new Set<string>();
    for (const u of personUpdates) {
      for (const id of u.newBadges) alreadyClaimedInFeed.add(id);
    }
    const grantByEventMs = new Map<number, string[]>();
    for (const id of profile.badgesIDs) {
      const dateAdded = BADGE_DESCRIPTIONS.get(id)?.dateAdded ?? DEFAULT_BADGE_DATE_ADDED;
      if (!isDateAddedVisible(dateAdded)) continue;
      const addedMs = parseDateAdded(dateAdded).getTime();
      const badge = badgesMap.get(id);

      if (isAutoClaimMilestoneBadge(badge)) {
        // Only “granted” when the badge was catalogued after they already met the deps
        // (e.g. Always Down for people who already had retreat stamps before it shipped).
        // Stamp at catalog date — not at a later claim. Skip if already on a claim card
        // or unlocked via a CUSTOM grant row (those stamp at the custom timestamp).
        if (customGrantedIds.has(id) || everOnSheet.has(id) || alreadyClaimedInFeed.has(id) || !badge) {
          continue;
        }
        let unlockMs: number | null = null;
        for (let i = 0; i < liveSorted.length; i++) {
          if (!autoClaimMilestoneDepsMet(badge, sheetIdSets[i])) continue;
          unlockMs = liveSorted[i].timestamp.getTime();
          break;
        }
        if (unlockMs == null || addedMs <= unlockMs) continue;
        const eventMs = grantMsAtCatalogOrJoin(addedMs, joinMs);
        const list = grantByEventMs.get(eventMs);
        if (list) list.push(id);
        else grantByEventMs.set(eventMs, [id]);
        continue;
      }

      // CUSTOM sheet diffs stamp below — don't also emit a catalog-date grant card.
      if (customGrantedIds.has(id)) continue;
      const grantOnly = isFeedGrantOnlyBadge(id, badgesMap);
      if (!grantOnly && everOnSheet.has(id)) continue;
      const isPostCatalogUnlock = addedMs > profile.lastUpdated.getTime();
      if (!grantOnly && !isPostCatalogUnlock) continue;
      const eventMs = grantEventMsForBadge(id, badgesMap, contribTimesSorted, joinMs);
      if (eventMs == null) continue;
      const list = grantByEventMs.get(eventMs);
      if (list) list.push(id);
      else grantByEventMs.set(eventMs, [id]);
    }
    // CUSTOM=`custom` sheet diffs: grant at the custom row’s timestamp.
    for (const id of customGrantedIds) {
      if (!profileBadgeIdSet.has(id) || alreadyClaimedInFeed.has(id)) continue;
      const eventMs = customGrantEventMsById.get(id);
      if (eventMs == null) continue;
      const list = grantByEventMs.get(eventMs);
      if (list) {
        if (!list.includes(id)) list.push(id);
      } else {
        grantByEventMs.set(eventMs, [id]);
      }
    }
    const grantEntries = [...grantByEventMs.entries()].sort((a, b) => a[0] - b[0]);
    const sheetOwnedAtMs = (ms: number): string[] => {
      let owned: string[] = [];
      for (let i = 0; i < liveSorted.length; i++) {
        if (liveSorted[i].timestamp.getTime() > ms) break;
        owned = currentOwnedByIndex[i] ?? owned;
      }
      return owned;
    };

    for (const [eventMs, ids] of grantEntries) {
      // One card per badge so same-badge catalog waves can merge across people
      // (e.g. World Walker recipients stay together even if someone also got Woodloch).
      // Ownership is “sheet as of this time + earlier grants”, not the final profile —
      // otherwise later claims make every early grant look like the journey was already done.
      const thisBatch = new Set(ids);
      const ownedSet = new Set(sheetOwnedAtMs(eventMs).filter((id) => !thisBatch.has(id)));
      for (const [gMs, gIds] of grantEntries) {
        if (gMs >= eventMs) break;
        for (const id of gIds) ownedSet.add(id);
      }
      let ownedSoFar = [...ownedSet];
      for (const id of ids) {
        const nextOwned = ownedSet.has(id) ? ownedSoFar : [...ownedSoFar, id];
        personUpdates.push({
          profile,
          eventAt: new Date(eventMs),
          newBadges: [id],
          ...bucketBadges([id], badgesMap),
          isNewUser: false,
          isAutoGrant: true,
          completedJourneys: journeysCompletedOnCard(ownedSoFar, nextOwned, [id]),
        });
        ownedSet.add(id);
        ownedSoFar = nextOwned;
      }
    }

    // Card chronology: credit a journey the first time feed-visible badges complete it.
    // (Does not attach to unrelated later claims — those cards don't touch the journey.)
    personUpdates.sort((a, b) => {
      const timeDiff = a.eventAt.getTime() - b.eventAt.getTime();
      if (timeDiff !== 0) return timeDiff;
      if (!!a.isAutoGrant !== !!b.isAutoGrant) return a.isAutoGrant ? -1 : 1;
      return 0;
    });
    const ownedFromCards = new Set<string>();
    for (const update of personUpdates) {
      const previousOwned = [...ownedFromCards];
      for (const id of update.newBadges) ownedFromCards.add(id);
      const fromCards = journeysCompletedOnCard(previousOwned, [...ownedFromCards], update.newBadges);
      if (fromCards.length === 0) continue;
      const have = new Set(update.completedJourneys.map((j) => j.id));
      const extra = fromCards.filter((j) => !have.has(j.id));
      if (extra.length > 0) update.completedJourneys = [...update.completedJourneys, ...extra];
    }

    updates.push(...personUpdates);
  });

  return mergeSamePersonSameDaySoloAutoGrants(
    mergeSameMomentAutoGrants(
      mergeUpdatesInBurstWindow(
        placeContributionAwardsAboveTheirCatalog(placeCatalogDropsBelowFirstClaims(updates)),
        badgesMap
      )
    ),
    badgesMap
  );
}

export function useFeedUpdates(
  submissionsByEmail: Map<string, RawSubmission[]>,
  profilesMap: Map<string, Profile>,
  badgesMap: Map<string, Badge>
): FeedUpdate[] {
  return useMemo(
    () => buildFeedUpdates(submissionsByEmail, profilesMap, badgesMap),
    [submissionsByEmail, profilesMap, badgesMap]
  );
}
